import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Hospital, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface RecordHospitalizationDialogProps {
  patientId: string;
  patientName: string;
  agencyId: string;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

export function RecordHospitalizationDialog({
  patientId,
  patientName,
  agencyId,
  trigger,
  onSuccess
}: RecordHospitalizationDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    event_type: 'admitted',
    facility_name: '',
    admission_reason: '',
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('patient_hospitalization_events')
        .insert({
          patient_id: patientId,
          agency_id: agencyId,
          event_type: formData.event_type,
          facility_name: formData.facility_name || null,
          admission_reason: formData.admission_reason || null,
          notes: formData.notes || null,
          recorded_by: user?.id || null
        });

      if (error) throw error;

      toast({
        title: formData.event_type === 'admitted' ? 'Hospitalization Recorded' : 'Discharge Recorded',
        description: `${patientName} has been marked as ${formData.event_type}.`
      });

      setOpen(false);
      setFormData({
        event_type: 'admitted',
        facility_name: '',
        admission_reason: '',
        notes: ''
      });
      onSuccess?.();
    } catch (error) {
      console.error('Error recording hospitalization:', error);
      toast({
        title: 'Error',
        description: 'Failed to record hospitalization event.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Hospital className="h-4 w-4" />
            Record Hospitalization
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hospital className="h-5 w-5" />
            Record Hospitalization Event
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium">Patient: {patientName}</p>
          </div>

          <div className="space-y-2">
            <Label>Event Type</Label>
            <Select
              value={formData.event_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admitted">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    Admitted to Hospital/ER
                  </span>
                </SelectItem>
                <SelectItem value="discharged">
                  <span className="flex items-center gap-2">
                    <Hospital className="h-4 w-4 text-green-500" />
                    Discharged from Hospital/ER
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="facility_name">Facility Name</Label>
            <Input
              id="facility_name"
              placeholder="e.g., City General Hospital"
              value={formData.facility_name}
              onChange={(e) => setFormData(prev => ({ ...prev, facility_name: e.target.value }))}
            />
          </div>

          {formData.event_type === 'admitted' && (
            <div className="space-y-2">
              <Label htmlFor="admission_reason">Reason for Admission</Label>
              <Input
                id="admission_reason"
                placeholder="e.g., Fall, Chest pain, etc."
                value={formData.admission_reason}
                onChange={(e) => setFormData(prev => ({ ...prev, admission_reason: e.target.value }))}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any additional information..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          {formData.event_type === 'admitted' && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Billing hours will be marked as paused for this patient.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Record Event'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
